<template>
  <v-dialog v-model="createProposalOpen" width="606" persistent>
    <v-card>
      <v-card-title>Credit Note Proposal</v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-autocomplete v-model="productType" label="Product Type" :items="productTypes" :error-messages="productTypeErrors" @change="$v.productType.$touch()" @blur="$v.productType.$touch()" :disabled="getCreateCreditNoteProposalLoading" outlined dense class="mb-n6"></v-autocomplete>
          </v-col>

          <v-col cols="12" md="4">
            <v-select v-model="shortReason" label="Short Reason" :items="getCreditNoteReasons" :error-messages="shortReasonErrors" @change="$v.shortReason.$touch()" @blur="$v.shortReason.$touch()" :disabled="getCreateCreditNoteProposalLoading||creditNoteReasonsLoading" :loading="creditNoteReasonsLoading" outlined dense class="mb-n6"></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-select v-model="department" label="Department" :items="getCreditNoteDepartments" :error-messages="departmentErrors" @change="$v.department.$touch()" @blur="$v.department.$touch()" :disabled="getCreateCreditNoteProposalLoading||creditNoteDepartmentsLoading" :loading="creditNoteDepartmentsLoading" outlined dense class="mb-n6"></v-select>
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="amount" label="Amount (Total)" prefix="£" :error-messages="amountErrors" @input="$v.amount.$touch()" @blur="$v.amount.$touch()" :disabled="disableAmount" outlined dense class="mb-n6"></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field v-model="subtotal" label="Subtotal" prefix="£" disabled outlined dense class="mb-n6"></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field v-model="vat" label="VAT" prefix="£" disabled outlined dense class="mb-n6"></v-text-field>
          </v-col>
        </v-row>
        
        <v-row>
          <v-col>
            <v-textarea v-model="detailedReason" label="Detailed Reason" :error-messages="detailedReasonErrors" @change="$v.detailedReason.$touch()" @blur="$v.detailedReason.$touch()" :disabled="getCreateCreditNoteProposalLoading" outlined dense class="mb-n6"></v-textarea>
          </v-col>
        </v-row>

        <template v-if="isSalesCreditSelected">
          <v-row>
            <v-col>
              <LineItemsTable />
            </v-col>
          </v-row>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>

        <template v-if="showError">
          <div class="mr-2 error--text text-uppercase">Please select at least 1 item</div>
        </template>

        <v-btn color="primary" small @click="submit" :disabled="getCreateCreditNoteProposalLoading" :loading="getCreateCreditNoteProposalLoading">SAVE</v-btn>
        <v-btn color="primary" small outlined @click="close">CANCEL</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { validationMixin } from "vuelidate";
  import { required, decimal } from "vuelidate/lib/validators";
  import { mapGetters,mapActions,mapState,mapMutations } from "vuex";
  import { addVat } from '@/helpers';

  import LineItemsTable from './LineItemsTable';

  export default {
    components: { LineItemsTable },

    mixins: [ validationMixin ],

    validations: {
      productType: { required },
      amount: { required, decimal },
      shortReason: { required },
      detailedReason: { required },
      department: { required },
    },

    data() {
      return {
        productType:'',
        amount:'',
        shortReason:'',
        detailedReason:'',
        department:'',
        productTypes:['VoIP','Telco','Website','Bundle'],
        showError: false,
        addVat
      }
    },

    computed: {
      ...mapState('accounts', ['currentAccount']),
      ...mapState('creditnotes',['createProposalOpen','creditNoteReasonsLoading','creditNoteDepartmentsLoading']),
      ...mapGetters('creditnotes',['getCreditNoteReasons','getCreditNoteDepartments', 'getCreateCreditNoteProposalLoading', 'getCreateCreditNoteProposalError', 'getLineItems', 'getTotalCreditAmount', 'getLineItemsForSubmission']),
      ...mapGetters('accounts', ['getCurrentAccount']),

      productTypeErrors() {
        const errors = [];
        if (!this.$v.productType.$dirty) return errors;
        !this.$v.productType.required && errors.push("Product type is required");
        return errors;
      },

      amountErrors() {
        const errors = [];
        if (!this.$v.amount.$dirty) return errors;
        !this.$v.amount.required && errors.push("Amount type is required");
        !this.$v.amount.decimal && errors.push("Amount must be a number");
        return errors;
      },

      shortReasonErrors() {
        const errors = [];
        if (!this.$v.shortReason.$dirty) return errors;
        !this.$v.shortReason.required && errors.push("Short reason type is required");
        return errors;
      },

      detailedReasonErrors() {
        const errors = [];
        if (!this.$v.detailedReason.$dirty) return errors;
        !this.$v.detailedReason.required && errors.push("Detailed reason type is required");
        return errors;
      },

      departmentErrors() {
        const errors = [];
        if (!this.$v.department.$dirty) return errors;
        !this.$v.department.required && errors.push("Department type is required");
        return errors;
      },

      vat() {
        let vat = this.amount - (this.amount / (1+this.$vatAmount));
        return vat.toFixed(2);
      },

      subtotal() {
        let total = this.amount - this.vat;
        return total.toFixed(2);
      },

      isSalesCreditSelected() {
        return this.shortReason === 'Sales Credit';
      },

      disableAmount() {
        return this.getCreateCreditNoteProposalLoading || this.isSalesCreditSelected;
      }
    },

    watch: {
      async createProposalOpen(open) {
        if(open){
          await this.fetchCreditNoteReasons();
          await this.fetchCreditNoteDepartments();
        }
      },

      isSalesCreditSelected(val) {
        if (val) {
          this.amount = this.addVat(this.getTotalCreditAmount);
          this.$v.amount.$reset();       
        } else {
          this.amount = '';
        }
      },

      getTotalCreditAmount(val) {
        if (this.isSalesCreditSelected) {
          this.amount = this.addVat(val);
        }
      }
    },

    methods: {
      ...mapActions('creditnotes',['fetchCreditNoteReasons','fetchCreditNoteDepartments','createCreditNoteProposal','fetchCreditNoteReasonsByAccount']),
      ...mapMutations('creditnotes',['setCreateProposalOpen', 'UPDATE_LINE_ITEMS']),

      async submit() {
        this.$v.$touch();
        this.validateLineItems();

        if(!this.$v.$error && !this.showError){
          let form = {
            product_type: this.productType,         
            short_reason: this.shortReason,
            detailed_reason: this.detailedReason,
            department: this.department,
          };

          if (!this.isSalesCreditSelected) {
            form.amount = -(this.amount);
          }

          if (this.isSalesCreditSelected) {
            form.line_items = this.getLineItemsForSubmission;
          }

          await this.createCreditNoteProposal({id:this.currentAccount.id,data:form});

          if (!this.getCreateCreditNoteProposalError) {
            this.fetchCreditNoteReasonsByAccount(this.currentAccount.id);
            this.close();
          }
        }
      },

      close() {
        this.reset();
        this.setCreateProposalOpen(false);
      },

      reset() {
        this.$v.$reset();
        this.productType = '';
        this.amount = '';
        this.shortReason = '';
        this.detailedReason = '';
        this.department = '';
        this.showError = false;
        this.UPDATE_LINE_ITEMS({lineItems: [], totalCreditAmount: 0});
      },

      validateLineItems() {
        this.showError = this.isSalesCreditSelected && this.getLineItems.length === 0 ? true : false;
      },
    },
  }
</script>

<style lang="scss" scoped></style>
